import {
  PROFILE_FIELDS, COURSE_COLS, BOOK_COLS, PAPER_COLS, FEEDBACK_COLS,
  GUARDIAN_FIELDS, TEXT_BLOCKS, ADMISSION_ROWS,
} from './faConstants';

const esc = (v) => (v == null || v === '' ? '' : String(v)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'));

const kv = (rows) => `<table class="kv">${rows.map(([k, v]) =>
  `<tr><th>${esc(k)}</th><td>${esc(v) || '—'}</td></tr>`).join('')}</table>`;

const grid = (cols, rows) => {
  const body = (rows && rows.length ? rows : [{}]).map((r, i) =>
    `<tr><td>${i + 1}</td>${cols.map(c => `<td>${esc(r[c.key]) || '—'}</td>`).join('')}</tr>`).join('');
  return `<table><thead><tr><th>Sr.</th>${cols.map(c => `<th>${esc(c.label)}</th>`).join('')}</tr></thead><tbody>${body}</tbody></table>`;
};

const block = (label, value) => {
  const v = Array.isArray(value)
    ? value.filter(x => x != null && String(x).trim() !== '').map((x, i) => `${i + 1}. ${x}`).join('\n')
    : value;
  return `<div class="blk"><div class="blabel">${esc(label)}</div><div class="bval">${esc(v) || '—'}</div></div>`;
};

/** Opens a print window with the consolidated appraisal report (staff form + Part II + Director). */
export function printAppraisalReport(data) {
  const { form = {}, cycle = {}, entity = {}, max_points: maxPoints = 700, eligible_cutoff: cutoff = 420 } = data || {};
  const a = form.part_a || {};
  const b = form.part_b || {};
  const ev = form.evaluation || {};
  const abs = form.hr_absence || {};
  const rev = form.review || {};
  const dir = form.director || {};

  const evRows = (ev.params || []).map(p =>
    `<tr><td>${p.sn}</td><td>${esc(p.label)}</td><td class="c">${p.score == null ? '—' : p.score}</td>
     <td class="c">${p.factor}</td><td class="c b">${p.points ?? 0}</td><td>${esc(p.remark)}</td></tr>`).join('');

  const admRows = (b.admissions && b.admissions.length ? b.admissions : ADMISSION_ROWS.map(d => ({ description: d })))
    .map(r => `<tr><td>${esc(r.year || b.admission_year || '2025-26')}</td><td>${esc(r.description)}</td>
      <td class="c">${esc(r.provisional) || '—'}</td><td class="c">${esc(r.actual) || '—'}</td></tr>`).join('');

  const revRows = (b.revenue && b.revenue.length ? b.revenue : [{}])
    .map(r => `<tr><td>${esc(r.sem)}</td><td>${esc(r.description)}</td>
      <td class="c">${esc(r.odd) || '—'}</td><td class="c">${esc(r.even) || '—'}</td></tr>`).join('');

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Performance Appraisal — ${esc(form.employee_name)}</title>
  <style>
    *{box-sizing:border-box}
    body{font-family:"Times New Roman",Georgia,serif;color:#111;margin:0;padding:18mm 14mm}
    h1{font-size:17px;text-align:center;margin:0 0 2px;text-transform:uppercase;letter-spacing:.5px}
    h2{font-size:13px;margin:22px 0 8px;padding:5px 8px;background:#e8e8e8;border-left:4px solid #b91c1c;text-transform:uppercase}
    h3{font-size:12px;margin:14px 0 5px;text-decoration:underline}
    .center{text-align:center}
    .meta{text-align:center;font-size:12px;margin-bottom:4px}
    table{width:100%;border-collapse:collapse;font-size:11px;margin-bottom:8px}
    th,td{border:1px solid #555;padding:4px 6px;text-align:left;vertical-align:top}
    th{background:#f2f2f2;font-weight:bold}
    table.kv th{width:32%;background:#fafafa}
    td.c{text-align:center}.b{font-weight:bold}
    .blk{margin-bottom:8px;font-size:11px}
    .blabel{font-weight:bold;text-transform:uppercase;font-size:10.5px}
    .bval{border:1px solid #777;min-height:26px;padding:4px 6px;white-space:pre-wrap}
    .total{font-size:13px;font-weight:bold;background:#f2f2f2;border:1px solid #555;padding:7px 9px;margin:8px 0}
    .sign{display:flex;justify-content:space-between;margin-top:26px;font-size:11px}
    .sign div{width:32%;border-top:1px solid #333;padding-top:4px;text-align:center}
    .badge{display:inline-block;padding:2px 8px;border:1px solid #333;font-weight:bold;font-size:11px}
    .pb{page-break-before:always}
    @media print{@page{size:A4 portrait;margin:12mm}}
  </style></head><body>
  <h1>Sanjay Ghodawat Group of Institutions</h1>
  <div class="meta">${esc(entity.name || a.institute || '')}</div>
  <h1 style="margin-top:8px">Performance Appraisal for Faculty</h1>
  <div class="meta">Period of Appraisal : Academic Year ${esc(form.academic_year || cycle.academic_year || '')}</div>

  <h2>Part – I (A) — Filled by Faculty</h2>
  ${kv(PROFILE_FIELDS.map(f => [f.label, a[f.key]]))}

  <h3>Academic Performance — Theory Courses</h3>
  ${grid(COURSE_COLS, a.theory_courses)}
  <h3>Academic Performance — Laboratory Courses</h3>
  ${grid(COURSE_COLS, a.lab_courses)}

  ${block(TEXT_BLOCKS[0].label, a.academic_qualification)}
  ${block('Reasons for Shortfall, if any, in Conduct & Result of Theory / Lab Courses', a.shortfall_reasons)}
  ${block('Contribution to Subject Learning Material Development', a.learning_material)}
  ${block('Contribution to the Laboratory Development', a.lab_development)}

  <h3>Performance as Faculty Guardian</h3>
  ${Array.isArray(a.faculty_guardian)
      ? grid(GUARDIAN_FIELDS, a.faculty_guardian)
      : kv(GUARDIAN_FIELDS.map(f => [f.label, (a.faculty_guardian || {})[f.key]]))}

  <h3>Publications — Books</h3>
  ${grid(BOOK_COLS, a.books)}
  <h3>Publications — Research Papers</h3>
  ${grid(PAPER_COLS, a.papers)}
  ${block('Google Scholar Profile Link', a.google_scholar_link)}

  <h3>Student Feedback</h3>
  ${grid(FEEDBACK_COLS, a.student_feedback)}

  ${TEXT_BLOCKS.slice(4).map(t => block(t.label, a[t.key])).join('')}

  <h3>Your Strengths and Weaknesses</h3>
  ${block('Strengths', a.strengths)}
  ${block('Weaknesses', a.weaknesses)}

  <div class="sign"><div>Signature of Faculty</div><div>Date : ${esc((form.submitted_at || '').slice(0, 10))}</div><div>&nbsp;</div></div>

  <h2 class="pb">Part – I (B) — Filled by Faculty</h2>
  ${kv([['Name of Faculty', a.name], ['Name of Head of Department', b.hod_name]])}
  <h3>Records of Admission</h3>
  <table><thead><tr><th>Year</th><th>Description</th><th>Provisional Admission Count</th><th>Actual Admitted Student Count</th></tr></thead><tbody>${admRows}</tbody></table>
  <h3>Revenue Generated for Department / Institute</h3>
  <table><thead><tr><th>Sem</th><th>Description</th><th>Revenue (Odd Sem) Rs.</th><th>Revenue (Even Sem) Rs.</th></tr></thead><tbody>${revRows}</tbody></table>

  <h3>Records of Absence (Filled by HR Department)</h3>
  <table><thead><tr><th>Sr.</th><th>Type of Leave</th><th>No. of Leaves Availed in A.Y. ${esc(form.academic_year || '')}</th><th>HR Name & Date</th></tr></thead>
  <tbody>
    <tr><td>1</td><td>Casual Leave (CL)</td><td class="c">${esc(abs.cl) || '—'}</td><td rowspan="4">${esc(abs.hr_name) || '—'}<br/>${esc(abs.hr_date) || ''}</td></tr>
    <tr><td>2</td><td>Medical Leave (ML)</td><td class="c">${esc(abs.ml) || '—'}</td></tr>
    <tr><td>3</td><td>Leave Without Pay (LWP)</td><td class="c">${esc(abs.lwp) || '—'}</td></tr>
    <tr><td>4</td><td>Total Late Marks in Year</td><td class="c">${esc(abs.late_marks) || '—'}</td></tr>
  </tbody></table>

  <h2 class="pb">Part – II — ${form.is_hod ? 'Evaluation by Director' : 'Evaluation by Head of Department'}</h2>
  ${kv([['Name of Faculty', form.employee_name], ['Department', form.department],
        ['Evaluated By', `${ev.evaluator_name || '—'}${ev.evaluator_role ? ` (${ev.evaluator_role})` : ''}`],
        ['Date of Evaluation', (ev.evaluated_at || '').slice(0, 10)]])}
  <table><thead><tr><th>S.N.</th><th>Parameter</th><th>Assessment on 5-point scale</th><th>Multiplying factor</th><th>Total Points</th><th>Remark</th></tr></thead>
  <tbody>${evRows || '<tr><td colspan="6">Not evaluated</td></tr>'}</tbody></table>
  <div class="total">Total Points Obtained : ${ev.total_points ?? 0} out of ${maxPoints}
    &nbsp;&nbsp;|&nbsp;&nbsp; ${ev.percentage != null ? ev.percentage + '%' : '—'}
    &nbsp;&nbsp;|&nbsp;&nbsp; <span class="badge">${ev.total_points >= cutoff ? 'ELIGIBLE FOR RECOMMENDATION' : 'BELOW MINIMUM ' + cutoff}</span></div>
  ${block('Overall Remark', ev.overall_remark)}
  ${ev.unsatisfactory_reasons ? block('Reasons for Unsatisfactory Rating', ev.unsatisfactory_reasons) : ''}
  <div class="sign"><div>${form.is_hod ? 'Director' : 'H.O.D.'} Signature</div><div>${esc(ev.evaluator_name)}</div><div>Date : ${esc((ev.evaluated_at || '').slice(0, 10))}</div></div>
  <p style="font-size:10.5px;margin-top:10px">Note: Minimum score of ${cutoff} out of ${maxPoints} makes a faculty eligible for recommendation.</p>

  <h2>Part – III — Reviewing Officer</h2>
  ${kv([['Name of Faculty', form.employee_name], ['Department', form.department],
        ['Assessment of Head of Department', rev.agreement === 'differ' ? 'I DIFFER with the assessment' : (rev.agreement ? 'I AGREE with the assessment' : '—')]])}
  ${rev.agreement === 'differ' ? block('Reasons', rev.reasons) : ''}

  <h2>Director — Final Remarks</h2>
  <div class="total">${dir.decision === 'recommended' ? 'RECOMMENDED' : dir.decision === 'not_recommended' ? 'NOT RECOMMENDED' : 'PENDING'}
    for annual increment / promotion for the next grade.</div>
  ${block('Remarks', dir.remarks)}
  <div class="sign"><div>(Institute Seal)</div><div>DIRECTOR<br/>${esc(dir.director_name)}</div><div>Date : ${esc((dir.approved_at || '').slice(0, 10))}</div></div>
  <p style="font-size:11px;margin-top:14px">Submitted to: The Hon. Trustee for the favor of approval.</p>
  </body></html>`;

  const w = window.open('', '_blank', 'width=1100,height=800');
  if (!w) return false;
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 500);
  return true;
}
